import React, { useMemo, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { allFlattenRoutes as routes } from './index';
import Enums from '../constants/enums';
import { isUserAuthenticated } from '../helpers/authUtils';
import AdminRoute from '../components/AdminRoute';
import ProtectedRoute from '../components/ProtectedRoute';
import RoleProtectedRoute from '../components/RoleProtectedRoute';

// All layouts/containers
import AuthLayout from '../layouts/Auth';
import VerticalLayout from '../layouts/vertical-layout/vertical-layout.page';

// Lazy-loaded page components for code-splitting
const EditAffiliatePage = React.lazy(() => import('../pages/settings/EditAffiliate'));
const EditAchLimitPage = React.lazy(() => import('../pages/resource/EditAchLimit'));
const ViewUserPage = React.lazy(() => import('../pages/user-administration/users/view'));
const EditUserPage = React.lazy(() => import('../pages/user-administration/users/edit'));
const TransferDeskDetailPage = React.lazy(() => import('../pages/inspect/transfer-desk-detail/transfer-desk-detail.page'));
const OutboundWireDetailPage = React.lazy(() => import('../pages/inspect/outbound-wire-detail/outbound-wire-detail.page'));
const GalileoUserDetailsPage = React.lazy(() => import('../pages/user-administration/users/galileodetails'));
const BankAccountsPage = React.lazy(() => import('../pages/user-administration/users/bankaccounts'));
const BankAccountEditPage = React.lazy(
    () => import('../pages/user-administration/users/bank-account-edit/bank-account-edit.page')
);
const StatementsPage = React.lazy(() => import('../pages/user-administration/users/statements'));
const CashbackDetailsPage = React.lazy(
    () => import('../pages/user-administration/users/cashback-details/cashback-details.page')
);
const NewUserPage = React.lazy(() => import('../pages/administravia/NewUser'));
const MyAccountPage = React.lazy(() => import('../pages/account/MyAccount'));
const ViewAccountPage = React.lazy(() => import('../pages/account/ViewAccount'));
const PermissionTemplateFormPage = React.lazy(() => import('../pages/administravia/ManagePermissions/PermissionTemplateForm'));
const ProBusinessAddMemberPage = React.lazy(() => import('../pages/pro-businesses/business-add-member/pro.business-add-member.page'));
const ProBusinessMembershipDetailsPage = React.lazy(() =>
    import('../pages/pro-businesses/business-membership-details/pro.business-membership-details.page')
);
const ProBusinessDetailsPage = React.lazy(() => import('../pages/pro-businesses/business-details/pro.business-details.page'));
const ConnectVendorCreatePage = React.lazy(() =>
    import('../pages/settings/connect-vendors/connect-vendor-create/connect-vendor-create.page'),
);
const ConnectVendorEditPage = React.lazy(() =>
    import('../pages/settings/connect-vendors/connect-vendor-edit/connect-vendor-edit.page'),
);

/** Same as `inspectorRoutes.roles` in routes/index.js (Transfer Desk list). */
const TRANSFER_DESK_ROLES = [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN];

const OUTBOUND_WIRES_ROLES = [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN];

/** Same as `proBusinessesRoute.roles` in routes/index.js. */
const PRO_BUSINESSES_ROLES = [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN];

/** Same as `appsettingsRoutes.roles` in routes/index.js (Connect venues). */
const CONNECT_VENDORS_ROLES = [Enums.UserRole.SUPPORT, Enums.UserRole.ADMIN];

/** Legacy transfer-inbox / wire-inbox URLs redirect to Transfer Desk. */
function TransferDeskLegacyDetailRedirect() {
    const { wireId } = useParams();
    return <Navigate to={`/inspect/transfer-desk/${wireId}`} replace />;
}

const AppRoutesInner = () => {
    const user = useSelector((state) => state.Auth.user);
    const location = useLocation();

    const isAuthenticated = useMemo(() => isUserAuthenticated(), [user]);

    // returns the layout
    const getLayout = (pathname) => {
        const accountAuthPaths = ['/account/login', '/account/login-token', '/account/login-verify', '/account/logout', '/account/forget-password'];
        const isAccountAuthPage =
            pathname.startsWith('/account/') &&
            (accountAuthPaths.includes(pathname) || pathname.startsWith('/account/reset-password'));
        if (!isAuthenticated || !user || isAccountAuthPage) {
            return AuthLayout;
        }
        return VerticalLayout;
    };

    // Memoize the layout to prevent unnecessary re-renders
    const Layout = useMemo(
        () => getLayout(location.pathname),
        [user, isAuthenticated, location.pathname]
    );

    // rendering the router with layout
    return (
        <Layout user={user}>
            <Suspense fallback={<div>Loading...</div>}>
                <Routes>
                    {/* Root route - redirect based on authentication */}
                    <Route
                        path="/"
                        element={
                            isAuthenticated ? (
                                <Navigate to="/dashboard" replace />
                            ) : (
                                <Navigate to="/account/login" replace />
                            )
                        }
                    />

                    {/* Protected Routes */}
                    <Route
                        path="/settings/edit/affiliate/:affiliate_id"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <EditAffiliatePage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/edit/ach-limit/:ach_limit_id"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <EditAchLimitPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/inspect/transfer-desk/:wireId"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={TRANSFER_DESK_ROLES}
                            >
                                <TransferDeskDetailPage />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/inspect/outbound-wires/:edgeTransactionId"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={OUTBOUND_WIRES_ROLES}
                            >
                                <OutboundWireDetailPage />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/inspect/transfer-inbox"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={TRANSFER_DESK_ROLES}
                            >
                                <Navigate to="/inspect/transfer-desk" replace />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/inspect/transfer-inbox/:wireId"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={TRANSFER_DESK_ROLES}
                            >
                                <TransferDeskLegacyDetailRedirect />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/inspect/wire-inbox"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={TRANSFER_DESK_ROLES}
                            >
                                <Navigate to="/inspect/transfer-desk" replace />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/inspect/wire-inbox/:wireId"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={TRANSFER_DESK_ROLES}
                            >
                                <TransferDeskLegacyDetailRedirect />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/view/:user_id"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <ViewUserPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/edit/:user_id"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <EditUserPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/view/:user_id/galileo"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <GalileoUserDetailsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/view/:user_id/bankaccounts"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <BankAccountsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/view/:user_id/bankaccounts/new"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <BankAccountEditPage mode="create" />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/view/:user_id/bankaccounts/:bankAccountId/edit"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <BankAccountEditPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/view/:user_id/statements"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <StatementsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/users/view/:user_id/cashback"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <CashbackDetailsPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/administravia/NewUser"
                        element={
                            <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                                <NewUserPage />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/account/my-account"
                        element={
                            <ProtectedRoute isAuthenticated={isAuthenticated}>
                                <MyAccountPage />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/account/:userId"
                        element={
                            <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                                <ViewAccountPage />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/administravia/permissions/new"
                        element={
                            <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                                <PermissionTemplateFormPage />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/administravia/permissions/:templateId"
                        element={
                            <AdminRoute isAuthenticated={isAuthenticated} user={user}>
                                <PermissionTemplateFormPage />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/settings/connect-venues/new"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={CONNECT_VENDORS_ROLES}
                            >
                                <ConnectVendorCreatePage />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/settings/connect-venues/:key/edit"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={CONNECT_VENDORS_ROLES}
                            >
                                <ConnectVendorEditPage />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/pro-businesses/:id/add-member"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={PRO_BUSINESSES_ROLES}
                            >
                                <ProBusinessAddMemberPage />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/pro-businesses/:id/members/:userId"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={PRO_BUSINESSES_ROLES}
                            >
                                <ProBusinessMembershipDetailsPage />
                            </RoleProtectedRoute>
                        }
                    />
                    <Route
                        path="/pro-businesses/:id"
                        element={
                            <RoleProtectedRoute
                                isAuthenticated={isAuthenticated}
                                user={user}
                                roles={PRO_BUSINESSES_ROLES}
                            >
                                <ProBusinessDetailsPage />
                            </RoleProtectedRoute>
                        }
                    />

                    {routes.map((route, index) => {
                        if (!route.children && route.component) {
                            return (
                                <Route
                                    key={index}
                                    path={route.path}
                                    element={
                                        route.path.startsWith('/account/') ? (
                                            <route.component {...route.props} />
                                        ) : (
                                            <RoleProtectedRoute
                                                isAuthenticated={isAuthenticated}
                                                user={user}
                                                roles={route.roles}
                                            >
                                                <route.component {...route.props} />
                                            </RoleProtectedRoute>
                                        )
                                    }
                                />
                            );
                        }
                        return null;
                    })}

                    {/* Catch-all - redirect based on authentication */}
                    <Route
                        path="*"
                        element={
                            isAuthenticated ? (
                                <Navigate to="/dashboard" replace />
                            ) : (
                                <Navigate to="/account/login" replace />
                            )
                        }
                    />
                </Routes>
            </Suspense>
        </Layout>
    );
};

const AppRoutes = () => {
    return (
        <BrowserRouter>
            <AppRoutesInner />
        </BrowserRouter>
    );
};

export default AppRoutes;
